import { useState } from 'react';
import {
  Buildings, CheckCircle, XCircle, Warning, SealCheck, ArrowRight,
} from '@phosphor-icons/react';
import { fmtDate } from '@/lib/utils';
import { useBootstrapPorVerificar, useVerificar } from '@/hooks/useBootstrap';
import type { PorVerificar } from '@/types';

// ============================================================
// Cola de verificación de lo auto-vinculado (B4, doc 9.5).
//
// Vista SEPARADA de "Por vincular" a propósito: allá se DECIDE, acá se LEE.
// Cada fila pone lo que el parser leyó del correo al lado del lote que el motor
// eligió — con eso alcanza para confirmar o rechazar de un vistazo, sin abrir
// nada. Es una rampa transitoria (9.0): cuando el equipo confíe en el
// auto-vínculo, esta cola deja de ser un paso obligatorio.
//
// Lista compacta, no tarjetas: son ~212 filas y el punto es pasarlas rápido.
// ============================================================

// Fila de la cola. El rechazo pide un segundo clic: en una pasada de clics
// rápidos, un "rechazar" por error devolvería el grupo a la bandeja.
function Fila({ t }: { t: PorVerificar }) {
  const verificar = useVerificar();
  const [confirmandoRechazo, setConfirmandoRechazo] = useState(false);

  const codigo = t.codigoCfia ?? t.codigoApc ?? '(sin código)';
  const tipo = t.codigoCfia ? 'CFIA' : t.codigoApc ? 'APC' : '—';
  const leido = t.textosLoteRaw.join(' · ');
  // Lo que el motor eligió vs lo que el parser leyó: si el texto crudo no es
  // igual al identificador del lote, la normalización hizo trabajo (K19 → K.19)
  // y vale la pena que el ojo se detenga ahí.
  const literal = t.textosLoteRaw.some(x => x.trim().toUpperCase() === (t.identificador ?? '').toUpperCase());

  if (verificar.isSuccess) {
    const rechazado = verificar.data.accion === 'rechazar';
    return (
      <div className="flex items-center gap-2 border-b border-brand-gray-200 px-2 py-2 text-xs text-brand-gray-400">
        {rechazado
          ? <XCircle size={16} weight="fill" className="text-brand-red-200" />
          : <SealCheck size={16} weight="fill" className="text-brand-green-200" />}
        <span className="font-mono font-semibold text-brand-gray-500">{codigo}</span>
        {rechazado
          ? <span>rechazado — {verificar.data.correosDevueltos} correos volvieron a la bandeja.</span>
          : <span>confirmado.</span>}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5 border-b border-brand-gray-200 px-2 py-2 md:flex-row md:items-center">
      {/* Código del trámite */}
      <div className="flex shrink-0 items-center gap-1.5 md:w-48">
        <span className="rounded bg-brand-gray-500 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
          {tipo}
        </span>
        <span className="font-mono text-sm font-semibold text-brand-gray-500">{codigo}</span>
        <span className="text-[10px] text-brand-gray-400">
          {t.cantidadCorreos} {t.cantidadCorreos === 1 ? 'correo' : 'correos'}
        </span>
      </div>

      {/* La comparación: lo leído → el lote elegido */}
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-xs">
        <span className="font-mono text-brand-gray-400" title="Texto de lote que el parser leyó del correo">
          {leido || <span className="italic">— sin texto —</span>}
        </span>
        <ArrowRight size={13} weight="bold" className="shrink-0 text-brand-gray-300" />
        <Buildings size={14} weight="bold" className="shrink-0 text-brand-gray-400" />
        <span className="font-mono text-sm font-semibold text-brand-gray-500">
          {t.idBd ?? `Lote ${t.idLote}`}
        </span>
        {t.identificador && (
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
            literal ? 'bg-brand-gray-100 text-brand-gray-400' : 'bg-brand-yellow/25 text-brand-gray-500'
          }`}>
            {t.identificador}
          </span>
        )}
        {t.proyecto && (
          <span className="rounded-full bg-brand-gray-100 px-2 py-0.5 text-[11px] text-brand-gray-400">{t.proyecto}</span>
        )}
      </div>

      {/* Contexto: modelo + estado inicial + fecha del último correo. TODO va
          etiquetado a propósito: sin rótulo, «Visado» se leía como un dato
          suelto y la fecha del batch se confundía con la del trámite. */}
      <div className="flex shrink-0 flex-col gap-0.5 text-[11px] leading-tight text-brand-gray-400 md:w-72">
        <span className="truncate">
          <span className="font-medium text-brand-gray-500">Modelo:</span>{' '}
          {t.modelo ?? <span className="italic">sin modelo</span>}
        </span>
        <span className="truncate">
          <span className="font-medium text-brand-gray-500">Estado inicial:</span>{' '}
          {t.estadoVigente ?? <span className="italic">—</span>}
        </span>
        <span className="truncate">
          <span className="font-medium text-brand-gray-500">Último correo:</span>{' '}
          <span className="font-mono">{fmtDate(t.fechaUltimoCorreo) || '—'}</span>
        </span>
      </div>

      {/* Acciones */}
      <div className="flex shrink-0 items-center gap-1.5">
        {confirmandoRechazo ? (
          <>
            <span className="text-[11px] text-brand-gray-400">¿Rechazar?</span>
            <button
              type="button"
              onClick={() => verificar.mutate({ idTramite: t.idTramite, accion: 'rechazar' })}
              disabled={verificar.isPending}
              className="rounded-lg bg-brand-red-100 px-2.5 py-1 text-xs font-semibold text-white hover:bg-brand-red-200 disabled:opacity-50"
            >
              Sí
            </button>
            <button
              type="button"
              onClick={() => setConfirmandoRechazo(false)}
              className="rounded-lg border border-brand-gray-300 bg-white px-2.5 py-1 text-xs text-brand-gray-500 hover:bg-brand-gray-100"
            >
              No
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => verificar.mutate({ idTramite: t.idTramite, accion: 'confirmar' })}
              disabled={verificar.isPending}
              className="inline-flex items-center gap-1 rounded-lg bg-brand-green-100 px-2.5 py-1 text-xs font-semibold text-black hover:bg-brand-green-200 disabled:opacity-50"
              title="El lote es el correcto: firma el trámite y sale de la cola"
            >
              <CheckCircle size={14} weight="bold" /> {verificar.isPending ? '…' : 'Confirmar'}
            </button>
            <button
              type="button"
              onClick={() => setConfirmandoRechazo(true)}
              disabled={verificar.isPending}
              className="inline-flex items-center gap-1 rounded-lg border border-brand-gray-300 bg-white px-2.5 py-1 text-xs text-brand-gray-500 hover:bg-brand-gray-100 disabled:opacity-50"
              title="El lote está mal: jubila el trámite y devuelve los correos a la bandeja"
            >
              <XCircle size={14} weight="bold" /> Rechazar
            </button>
          </>
        )}
      </div>

      {verificar.isError && (
        <div className="flex items-start gap-1.5 text-[11px] text-brand-red-200 md:w-full">
          <Warning size={13} weight="bold" className="mt-0.5 shrink-0" />
          {(verificar.error as Error)?.message ?? 'Error al verificar'}
        </div>
      )}
    </div>
  );
}

export function VerificarList() {
  const { data, isLoading, isError, error } = useBootstrapPorVerificar();

  if (isLoading) return <p className="text-sm text-brand-gray-400">Cargando la cola de verificación…</p>;
  if (isError) {
    return (
      <p className="text-sm text-brand-red-200">
        Error al cargar la cola: {(error as Error)?.message}
      </p>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="rounded-2xl border border-brand-gray-200 bg-white p-10 text-center">
        <SealCheck size={40} weight="bold" className="mx-auto mb-3 text-brand-gray-300" />
        <p className="font-medium text-brand-gray-500">Nada por verificar</p>
        <p className="text-sm text-brand-gray-400">
          No hay trámites auto-vinculados pendientes de confirmación.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* UNA sola pregunta, arriba y en grande. Lo demás es contexto: si el
          operador tiene que deducir qué se le está preguntando, la cola no
          avanza. */}
      <div className="mb-3 max-w-3xl rounded-2xl border border-brand-gray-200 bg-white p-3.5">
        <p className="text-sm font-semibold text-brand-gray-500">
          ¿El lote que eligió la máquina es el lote correcto?
        </p>
        <p className="mt-1 text-sm text-brand-gray-400">
          Es la única pregunta. A la izquierda está el <strong>texto que el parser leyó del correo</strong>;
          a la derecha, el <strong>lote que el motor eligió</strong>. Si coinciden,{' '}
          <strong>Confirmar</strong> (firma el trámite y sale de la cola). Si no,{' '}
          <strong>Rechazar</strong> lo jubila y devuelve sus correos a la bandeja, para vincularlos a mano
          al lote correcto.
        </p>
        <p className="mt-1.5 text-[11px] text-brand-gray-400">
          El modelo y el estado inicial <strong>no se verifican acá</strong>: se corrigen después en el
          trámite. La pastilla del lote en{' '}
          <span className="rounded-full bg-brand-yellow/25 px-1.5 py-0.5 text-brand-gray-500">amarillo</span>{' '}
          avisa que el texto del correo no era idéntico al identificador del lote (hubo normalización, ej.
          «K19» → «K.19») — ahí vale detener el ojo.
        </p>
      </div>
      <div className="overflow-hidden rounded-2xl border border-brand-gray-200 bg-white">
        {data.map(t => <Fila key={t.idTramite} t={t} />)}
      </div>
    </>
  );
}
